import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import SelectorColor from '../../components/SelectorColor'
import SelectorNegocio from '../../components/SelectorNegocio'

/**
 * Editor de etapas del pipeline.
 *
 * El backend ya tenía el CRUD completo de etapas, pero no había forma de
 * usarlo desde el panel: las columnas del kanban solo se dibujaban, así que
 * nadie podía renombrarlas, cambiarles el color ni añadir una nueva.
 */
function EditorEtapas({ pipeline, onCerrar, onGuardado }) {
  const [etapas, setEtapas] = useState(
    [...(pipeline.Etapas || [])].sort((a, b) => a.orden - b.orden)
  )
  const [guardando, setGuardando] = useState(false)

  const upd = (id, campo, valor) =>
    setEtapas(es => es.map(e => (e.id === id ? { ...e, [campo]: valor, _tocada: true } : e)))

  const agregar = () => setEtapas(es => [...es, {
    id: `nueva-${Date.now()}`,
    nombre: '', color: '#3b82f6', orden: es.length,
    esCierreGanado: false, esCierrePerdido: false, probabilidad: 50,
    _nueva: true,
  }])

  const quitar = (id) => setEtapas(es => es.filter(e => e.id !== id))

  const mover = (idx, dir) => setEtapas(es => {
    const destino = idx + dir
    if (destino < 0 || destino >= es.length) return es
    const copia = [...es]
    ;[copia[idx], copia[destino]] = [copia[destino], copia[idx]]
    return copia.map((e, i) => ({ ...e, orden: i, _tocada: true }))
  })

  const guardar = async () => {
    if (etapas.some(e => !e.nombre.trim())) {
      toast.error('Todas las etapas necesitan un nombre')
      return
    }
    setGuardando(true)
    try {
      const originales = pipeline.Etapas || []
      // Borrar las que el usuario quitó
      const vivas = new Set(etapas.map(e => e.id))
      for (const o of originales) {
        if (!vivas.has(o.id)) await api.delete(`/pipelines/etapas/${o.id}`)
      }
      // Crear y actualizar
      for (const [i, e] of etapas.entries()) {
        const datos = {
          nombre: e.nombre.trim(), color: e.color, orden: i,
          esCierreGanado: !!e.esCierreGanado, esCierrePerdido: !!e.esCierrePerdido,
          probabilidad: e.probabilidad ?? 50,
        }
        if (e._nueva) await api.post('/pipelines/etapas', { ...datos, pipelineId: pipeline.id })
        else if (e._tocada) await api.put(`/pipelines/etapas/${e.id}`, datos)
      }
      toast.success('Etapas guardadas')
      onGuardado()
      onCerrar()
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudieron guardar las etapas')
    } finally { setGuardando(false) }
  }

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Etapas de {pipeline.nombre}</span>
          <button className="btn btn-ghost btn-icon" onClick={onCerrar}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {etapas.map((e, i) => (
            <div key={e.id} style={{
              border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              padding: 14, marginBottom: 12, borderLeft: `4px solid ${e.color}`,
            }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <input
                  className="input" value={e.nombre} placeholder="Nombre de la etapa"
                  onChange={ev => upd(e.id, 'nombre', ev.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button className="btn btn-ghost btn-icon" title="Subir"
                  onClick={() => mover(i, -1)} disabled={i === 0}>↑</button>
                <button className="btn btn-ghost btn-icon" title="Bajar"
                  onClick={() => mover(i, 1)} disabled={i === etapas.length - 1}>↓</button>
                <button className="btn btn-ghost btn-icon" title="Eliminar etapa"
                  onClick={() => quitar(e.id)}>🗑️</button>
              </div>

              <label className="form-label" style={{ fontSize: 12 }}>Color</label>
              <div style={{ marginTop: 6, marginBottom: 12 }}>
                <SelectorColor value={e.color} onChange={c => upd(e.id, 'color', c)} />
              </div>

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!e.esCierreGanado}
                    onChange={ev => {
                      upd(e.id, 'esCierreGanado', ev.target.checked)
                      if (ev.target.checked) upd(e.id, 'esCierrePerdido', false)
                    }} />
                  ✅ Cierre ganado
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!e.esCierrePerdido}
                    onChange={ev => {
                      upd(e.id, 'esCierrePerdido', ev.target.checked)
                      if (ev.target.checked) upd(e.id, 'esCierreGanado', false)
                    }} />
                  ❌ Cierre perdido
                </label>
              </div>
            </div>
          ))}

          <button className="btn btn-secondary" onClick={agregar} style={{ width: '100%' }}>
            + Añadir etapa
          </button>

          <div className="form-hint" style={{ marginTop: 12 }}>
            Los prospectos que registra el bot entran en la primera etapa que no sea de cierre.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onCerrar}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar etapas'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Pipeline() {
  const [pipelines, setPipelines] = useState([])
  const [activo, setActivo] = useState(null)
  const [negocios, setNegocios] = useState([])
  const [showNuevo, setShowNuevo] = useState(false)
  const [contactos, setContactos] = useState([])
  const [form, setForm] = useState({ titulo: '', valor: 0, contactoId: '', etapaId: '' })
  const [editandoEtapas, setEditandoEtapas] = useState(false)
  const [negocioFiltro, setNegocioFiltro] = useState('todos')

  // Al cambiar de negocio se recargan los tableros: cada marca tiene los suyos.
  useEffect(() => { cargarPipelines() }, [negocioFiltro])

  const cargarPipelines = async () => {
    const { data } = await api.get('/pipelines', { params: { unidadNegocioId: negocioFiltro } })
    setPipelines(data)
    // Si el tablero abierto no pertenece al negocio elegido, se pasa al primero
    // que sí: quedarse en uno que ya no está en la lista muestra un kanban vacío.
    const sigueVisible = activo && data.some(p => p.id === activo.id)
    if (data.length > 0 && !sigueVisible) {
      setActivo(data[0])
      cargarNegocios(data[0].id)
    } else if (data.length === 0) {
      setActivo(null)
      crearPipelineDefault()
    }
  }

  const crearPipelineDefault = async () => {
    const { data } = await api.post('/pipelines', { nombre: 'Pipeline de ventas', esDefault: true })
    setActivo(data)
    setPipelines([data])
    cargarNegocios(data.id)
  }

  // Tablero nuevo para el negocio que esté elegido arriba. Con "Todos" sale un
  // tablero compartido, que es como se comportaba el pipeline hasta ahora.
  const crearPipeline = async () => {
    const nombre = window.prompt('Nombre del tablero nuevo', 'Pipeline de servicios')
    if (!nombre?.trim()) return
    try {
      const { data } = await api.post('/pipelines', {
        nombre: nombre.trim(),
        ...(negocioFiltro && negocioFiltro !== 'todos' ? { unidadNegocioId: negocioFiltro } : {}),
      })
      await cargarPipelines()
      setActivo(data)
      cargarNegocios(data.id)
      toast.success(negocioFiltro && negocioFiltro !== 'todos'
        ? 'Tablero creado para este negocio'
        : 'Tablero creado (compartido por toda la empresa)')
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo crear el tablero')
    }
  }

  const cargarNegocios = async (pid) => {
    const { data } = await api.get(`/pipelines/${pid}/negocios`)
    setNegocios(data)
  }

  useEffect(() => {
    api.get('/contactos').then(r => setContactos(r.data.contactos || []))
  }, [])

  const onDragStart = (e, neg) => e.dataTransfer.setData('negocioId', neg.id)
  const onDrop = async (e, etapa) => {
    e.preventDefault()
    const id = e.dataTransfer.getData('negocioId')
    await api.post(`/pipelines/negocios/${id}/mover`, { etapaId: etapa.id })
    cargarNegocios(activo.id)
    toast.success('Movido a ' + etapa.nombre)
  }
  const onDragOver = (e) => e.preventDefault()

  const crearNegocio = async (e) => {
    e.preventDefault()
    try {
      await api.post('/pipelines/negocios', { ...form, pipelineId: activo.id })
      setShowNuevo(false)
      setForm({ titulo: '', valor: 0, contactoId: '', etapaId: '' })
      cargarNegocios(activo.id)
      toast.success('Negocio creado')
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error')
    }
  }

  if (!activo) return <div className="loading-fullscreen"><div className="spinner" /></div>

  return (
    <div className="page" style={{ paddingBottom: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="flex justify-between items-center" style={{ marginBottom: 14 }}>
        <div>
          <h1 className="text-2xl font-bold">📊 Pipeline</h1>
          <select className="select" style={{ width: 'auto', marginTop: 4 }} value={activo.id} onChange={e => { const p = pipelines.find(x => x.id === e.target.value); setActivo(p); cargarNegocios(p.id) }}>
            {pipelines.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
          <div style={{ marginTop: 10 }}>
            <SelectorNegocio valor={negocioFiltro} onChange={setNegocioFiltro} clave="pipeline" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={crearPipeline}>+ Tablero</button>
          <button className="btn btn-secondary" onClick={() => setEditandoEtapas(true)}>🎨 Etapas</button>
          <button className="btn btn-primary" onClick={() => setShowNuevo(true)}>+ Nuevo negocio</button>
        </div>
      </div>

      <div className="kanban" style={{ flex: 1, paddingTop: 0 }}>
        {(activo.Etapas || []).sort((a, b) => a.orden - b.orden).map(etapa => {
          const items = negocios.filter(n => n.etapaId === etapa.id)
          const total = items.reduce((sum, n) => sum + parseFloat(n.valor || 0), 0)
          return (
            <div key={etapa.id} className="kanban-column" onDragOver={onDragOver} onDrop={(e) => onDrop(e, etapa)}>
              <div className="kanban-col-header" style={{ borderTop: `3px solid ${etapa.color}` }}>
                <span>{etapa.nombre}</span>
                <span className="badge">{items.length} · ${total.toFixed(0)}</span>
              </div>
              <div className="kanban-col-body">
                {items.map(neg => (
                  <div key={neg.id} className="kanban-card" draggable onDragStart={(e) => onDragStart(e, neg)}>
                    <div className="font-semibold text-sm">{neg.titulo}</div>
                    <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                      💵 ${parseFloat(neg.valor).toFixed(2)}
                    </div>
                    {neg.Contacto && <div className="text-sm" style={{ marginTop: 4 }}>👤 {neg.Contacto.nombre}</div>}
                    {neg.agente && <div className="text-sm text-muted" style={{ marginTop: 2 }}>🧑‍💼 {neg.agente.nombre}</div>}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {editandoEtapas && (
        <EditorEtapas
          pipeline={activo}
          onCerrar={() => setEditandoEtapas(false)}
          onGuardado={async () => {
            const { data } = await api.get('/pipelines')
            setPipelines(data)
            const actualizado = data.find(p => p.id === activo.id)
            if (actualizado) setActivo(actualizado)
          }}
        />
      )}

      {showNuevo && (
        <div className="modal-overlay" onClick={() => setShowNuevo(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Nuevo negocio</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowNuevo(false)}>✕</button>
            </div>
            <form onSubmit={crearNegocio}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Título</label>
                  <input className="input" required value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Valor (USD)</label>
                  <input type="number" step="0.01" className="input" value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Contacto</label>
                  <select className="select" required value={form.contactoId} onChange={e => setForm({...form, contactoId: e.target.value})}>
                    <option value="">-- Seleccionar --</option>
                    {contactos.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.telefono})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Etapa</label>
                  <select className="select" required value={form.etapaId} onChange={e => setForm({...form, etapaId: e.target.value})}>
                    <option value="">-- Seleccionar --</option>
                    {(activo.Etapas || []).sort((a, b) => a.orden - b.orden).map(et => <option key={et.id} value={et.id}>{et.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setShowNuevo(false)}>Cancelar</button>
                <button className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
